import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Plus, Trash2, FileText, X, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { renderMarkdown } from "@/lib/md";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const fmt = (n) => "$" + Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 });

const HEALTH_CLR = { green: "bg-emerald-400", amber: "bg-gold-500", red: "bg-red-500", grey: "bg-white/25" };
const HEALTH_TXT = { green: "text-emerald-300", amber: "text-gold-300", red: "text-red-300", grey: "text-white/50" };
const HEALTH_RULES = {
  green: "Spend below 85% of budget and forecast below 95%.",
  amber: "Spend at/above 85% of budget, or forecast at/above 95%.",
  red: "Spend or forecast has exceeded the approved budget.",
  grey: "No budget set — health cannot be assessed.",
};

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [risks, setRisks] = useState([]);
  const [report, setReport] = useState(null);
  const [genning, setGenning] = useState(false);
  const [error, setError] = useState("");
  const [showRisk, setShowRisk] = useState(false);
  const [healthInfo, setHealthInfo] = useState(null);
  const [risk, setRisk] = useState({ title: "", category: "Delivery", likelihood: 3, consequence: 3, controls: "", owner: "" });

  const load = () => {
    api.get("/portfolio").then((r) => setData(r.data)).catch(() => {});
    api.get("/risks").then((r) => setRisks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addRisk = async (e) => {
    e.preventDefault();
    await api.post("/risks", { ...risk, likelihood: Number(risk.likelihood), consequence: Number(risk.consequence) });
    setShowRisk(false);
    setRisk({ title: "", category: "Delivery", likelihood: 3, consequence: 3, controls: "", owner: "" });
    load();
  };
  const delRisk = async (id) => { await api.delete(`/risks/${id}`); load(); };
  const cycleRisk = async (r) => {
    const order = ["open", "mitigating", "closed"];
    await api.patch(`/risks/${r.id}`, { status: order[(order.indexOf(r.status) + 1) % order.length] });
    load();
  };

  const genReport = async () => {
    setError(""); setGenning(true);
    try {
      const res = await api.post("/portfolio/report");
      setReport(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setGenning(false); }
  };

  if (!data) return <Mono className="text-gold-500/60">Loading…</Mono>;

  const kpis = [
    { label: "Total Budget", value: fmt(data.total_budget) },
    { label: "Spent", value: `${fmt(data.total_spent)} · ${data.spent_pct}%` },
    { label: "Forecast (FAC)", value: fmt(data.total_forecast) },
    { label: "Variance", value: fmt(data.variance), warn: data.variance < 0 },
    { label: "Cash-flow Exposure", value: fmt(data.exposure), warn: data.exposure > 0 },
    { label: "Projects", value: data.project_count },
  ];

  return (
    <div className="space-y-10 text-white" data-testid="portfolio-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">Sheet 03 — Portfolio Management &amp; Reporting</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Program control &amp; executive reporting</h1>
          <p className="text-white/55 mt-3 max-w-2xl font-light">
            Portfolio-wide expenditure, cash-flow forecasting, contingency early-warnings and a risk
            register — the "system for managing a portfolio of multiple projects" panels ask for.
          </p>
        </div>
        <button
          onClick={genReport}
          disabled={genning}
          data-testid="generate-report-btn"
          className="bg-gold-500 text-navy-950 font-semibold px-6 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {genning ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          {genning ? "Generating…" : "Monthly executive report"}
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3">{error}</div>}

      {/* KPI cells */}
      <div className="grid grid-cols-2 lg:grid-cols-6 border-t border-l border-gold-500/25">
        {kpis.map((k) => (
          <div key={k.label} className="border-r border-b border-gold-500/25 bg-navy-900/40 p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <Mono className="text-white/40 text-[0.5rem] block">{k.label}</Mono>
            <div className={`font-mono text-xl mt-2 ${k.warn ? "text-red-400" : "text-white"}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* health + warnings */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="border border-gold-500/25 bg-navy-900/40 p-6" data-testid="programme-health-card">
          <Mono className="text-gold-400 text-[0.6rem] block mb-4">Programme Health</Mono>
          {["green", "amber", "red", "grey"].map((c) => (
            <div key={c} className="flex items-start gap-3 mb-3" title={HEALTH_RULES[c]}>
              <span className={`h-3 w-3 mt-1 ${HEALTH_CLR[c]}`} />
              <div className="flex-1">
                <span className="capitalize text-white/70 text-sm">{c === "grey" ? "no budget set" : c}</span>
                <p className="text-white/35 text-[0.65rem] font-light leading-snug">{HEALTH_RULES[c]}</p>
              </div>
              <span className="font-mono text-white">{data.health[c]}</span>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 border border-gold-500/25 bg-navy-900/40 p-6">
          <Mono className="text-gold-400 text-[0.6rem] block mb-4">Early Warnings ({data.warnings.length})</Mono>
          {data.warnings.length === 0 ? (
            <p className="text-white/45 text-sm font-light">No cash-flow or contingency warnings. Portfolio within tolerance.</p>
          ) : (
            <div className="space-y-3">
              {data.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`warning-${i}`}>
                  <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${w.level === "high" ? "text-red-400" : "text-gold-400"}`} />
                  <div>
                    <span className="text-white text-sm font-medium">{w.project}</span>
                    <span className="text-white/55 text-sm font-light"> — {w.message}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* financial table */}
      <div>
        <Mono className="text-gold-400 text-[0.6rem] block mb-4">Project Financials</Mono>
        {data.projects.length === 0 ? (
          <div className="border border-dashed border-gold-500/25 p-10 text-center text-white/45 text-sm font-light">
            No projects yet. Add projects (with budgets) to see portfolio control.
          </div>
        ) : (
          <div className="border border-gold-500/25 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy-900/60 border-b border-gold-500/20 text-left">
                  {["Health", "Project", "Budget", "Spent", "Forecast", "Variance", "Funding"].map((h) => (
                    <th key={h} className="px-4 py-3"><Mono className="text-white/40 text-[0.5rem]">{h}</Mono></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.projects.map((p) => {
                  const v = (p.budget || 0) - ((p.forecast || 0) || (p.budget || 0));
                  return (
                    <tr key={p.id} className="border-b border-gold-500/10" data-testid={`pf-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setHealthInfo(p)} data-testid={`health-why-${p.id}`} title="Why this health status?"
                          className="group inline-flex items-center gap-1.5">
                          <span className={`h-2.5 w-2.5 inline-block ${HEALTH_CLR[p.health]}`} />
                          <Info className="h-3.5 w-3.5 text-white/20 group-hover:text-gold-400 transition-colors" />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-white">{p.name}</td>
                      <td className="px-4 py-3 font-mono text-white/80">{fmt(p.budget)}</td>
                      <td className="px-4 py-3 font-mono text-white/80">{fmt(p.spent)}</td>
                      <td className="px-4 py-3 font-mono text-white/80">{fmt(p.forecast)}</td>
                      <td className={`px-4 py-3 font-mono ${v < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmt(v)}</td>
                      <td className="px-4 py-3 text-white/50 text-xs">{p.funding_source || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* risk register */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <Mono className="text-gold-400 text-[0.6rem]">Portfolio Risk Register (ISO 31000)</Mono>
          <button onClick={() => setShowRisk(true)} data-testid="add-risk-btn" className="text-sm text-gold-400 hover:text-gold-300 flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add risk
          </button>
        </div>
        <div className="border border-gold-500/25 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-navy-900/60 border-b border-gold-500/20 text-left">
                {["Risk", "Category", "L×C", "Rating", "Owner", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3"><Mono className="text-white/40 text-[0.5rem]">{h}</Mono></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {risks.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-white/40 text-sm font-light">No risks logged.</td></tr>
              )}
              {risks.map((r) => (
                <tr key={r.id} className="border-b border-gold-500/10" data-testid={`risk-row-${r.id}`}>
                  <td className="px-4 py-3 text-white">{r.title}</td>
                  <td className="px-4 py-3 text-white/60">{r.category}</td>
                  <td className="px-4 py-3 font-mono text-white/70">{r.likelihood}×{r.consequence}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono px-2 py-0.5 text-xs ${r.rating >= 15 ? "bg-red-500/20 text-red-300" : r.rating >= 8 ? "bg-gold-500/20 text-gold-300" : "bg-emerald-500/15 text-emerald-300"}`}>{r.rating}</span>
                  </td>
                  <td className="px-4 py-3 text-white/60 text-xs">{r.owner || "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => cycleRisk(r)} data-testid={`risk-status-${r.id}`} className="font-mono text-[0.6rem] uppercase tracking-widest text-white/70 hover:text-gold-400">{r.status}</button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => delRisk(r.id)} data-testid={`del-risk-${r.id}`} className="text-white/30 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* health transparency modal */}
      {healthInfo && (
        <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={() => setHealthInfo(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-md p-6 space-y-4" data-testid="health-why-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-white">Why is this {healthInfo.health === "grey" ? "unrated" : healthInfo.health}?</h2>
              <button onClick={() => setHealthInfo(null)} data-testid="health-why-close"><X className="h-5 w-5 text-white/60" /></button>
            </div>
            <div className="flex items-center gap-2.5">
              <span className={`h-3 w-3 ${HEALTH_CLR[healthInfo.health]}`} />
              <span className="text-white text-sm font-medium">{healthInfo.name}</span>
              <span className={`font-mono text-[0.6rem] uppercase tracking-widest ${HEALTH_TXT[healthInfo.health]}`}>{healthInfo.health}</span>
            </div>
            <div className="space-y-2.5" data-testid="health-reasons">
              {(healthInfo.health_reasons || []).map((r, i) => (
                <div key={i} className="flex items-start gap-2.5 border border-gold-500/15 bg-navy-950 px-3 py-2.5">
                  <Info className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${HEALTH_TXT[healthInfo.health]}`} />
                  <p className="text-white/70 text-sm font-light leading-snug">{r}</p>
                </div>
              ))}
            </div>
            <p className="text-white/35 text-[0.65rem] font-light border-t border-gold-500/15 pt-3">
              Thresholds: green = spend &lt; 85% and forecast &lt; 95% of budget · amber = spend ≥ 85% or forecast ≥ 95% · red = spend or forecast over budget · grey = no budget set.
            </p>
          </div>
        </div>
      )}

      {/* report modal */}
      {report && (
        <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={() => setReport(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-3xl max-h-[85vh] flex flex-col" data-testid="report-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gold-500/20">
              <h2 className="font-heading text-xl text-white">{report.title}</h2>
              <button onClick={() => setReport(null)}><X className="h-5 w-5 text-white/60" /></button>
            </div>
            <div className="overflow-y-auto p-2">
              <div className="bg-white p-8 md-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(report.content) }} />
            </div>
            <div className="px-6 py-3 border-t border-gold-500/20">
              <Mono className="text-white/40 text-[0.55rem]">Saved to AI Documents · export/print from your browser</Mono>
            </div>
          </div>
        </div>
      )}

      {/* add-risk modal */}
      {showRisk && (
        <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={() => setShowRisk(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={addRisk} className="bg-navy-900 border border-gold-500/30 w-full max-w-lg p-8 space-y-5" data-testid="risk-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-white">Add portfolio risk</h2>
              <button type="button" onClick={() => setShowRisk(false)}><X className="h-5 w-5 text-white/60" /></button>
            </div>
            <input required value={risk.title} onChange={(e) => setRisk({ ...risk, title: e.target.value })} placeholder="Risk description" data-testid="risk-title" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 placeholder:text-white/30" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Category</Mono>
                <select value={risk.category} onChange={(e) => setRisk({ ...risk, category: e.target.value })} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500">
                  {["Delivery", "Financial", "WHS", "Programme", "Commercial", "Compliance"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Owner</Mono>
                <input value={risk.owner} onChange={(e) => setRisk({ ...risk, owner: e.target.value })} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500" />
              </div>
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Likelihood (1-5)</Mono>
                <input type="number" min="1" max="5" value={risk.likelihood} onChange={(e) => setRisk({ ...risk, likelihood: e.target.value })} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500" />
              </div>
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Consequence (1-5)</Mono>
                <input type="number" min="1" max="5" value={risk.consequence} onChange={(e) => setRisk({ ...risk, consequence: e.target.value })} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500" />
              </div>
            </div>
            <textarea rows={2} value={risk.controls} onChange={(e) => setRisk({ ...risk, controls: e.target.value })} placeholder="Controls / mitigation" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 resize-none placeholder:text-white/30" />
            <button type="submit" data-testid="save-risk-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-3 rounded-sm hover:bg-gold-400 transition-colors">Add risk</button>
          </form>
        </div>
      )}
    </div>
  );
}
